import { createContext, memo, useContext, useMemo } from "react";
import { Handle, Position, type Node, type NodeProps } from "@xyflow/react";
import { ReadOnlyFlow } from "../echoflicks/journey/ReadOnlyFlow";
import {
  buildPipelineGraph,
  type PipelineNodeData,
} from "./buildPipelineGraph";
import { label } from "./format";
import type { MonitorStage, QueueRunDetail } from "./types";
import { PipelineEdge } from "./PipelineEdge";

const Interactions = createContext<{
  inspect: (stage: MonitorStage, pin: boolean) => void;
  expand: (group: string) => void;
  previewGroup: (group: string) => void;
}>({ inspect: () => {}, expand: () => {}, previewGroup: () => {} });
const PipelineNode = memo(function PipelineNode({
  data,
}: NodeProps<Node<PipelineNodeData>>) {
  const { inspect, expand, previewGroup } = useContext(Interactions);
  const preview = () => {
    if (data.stage) inspect(data.stage, false);
    else if (data.group) previewGroup(data.group);
  };
  return (
    <div className="w-56 rounded-lg border bg-card shadow-sm">
      {(data.stage || !!data.count) && (
        <Handle type="target" position={Position.Left} />
      )}
      <button
        type="button"
        className="nodrag nopan w-full rounded-lg p-3 text-left focus-visible:outline-2 focus-visible:outline-primary"
        onFocus={preview}
        onMouseEnter={preview}
        onClick={() =>
          data.stage
            ? inspect(data.stage, true)
            : data.group && expand(data.group)
        }
      >
        <span className="block text-sm font-semibold">{data.label}</span>
        <span
          className={`mt-1 block text-xs ${data.state === "failed" ? "text-destructive" : "text-muted-foreground"}`}
        >
          {label(data.state)}
        </span>
        {data.group && (
          <span className="mt-2 block text-xs text-muted-foreground">
            {data.count
              ? `${data.count} observed steps · Expand`
              : "No observations yet"}
          </span>
        )}
        {data.stage?.wait_reason && (
          <span className="block text-xs">
            Waiting: {label(data.stage.wait_reason)}
          </span>
        )}
        {data.stage?.progress && (
          <span className="block text-xs">
            {data.stage.progress.completed}/{data.stage.progress.total}{" "}
            {data.stage.progress.unit}
          </span>
        )}
      </button>
      {(data.stage || !!data.count) && (
        <Handle type="source" position={Position.Right} />
      )}
      {data.stage && (
        <Handle
          id="contains"
          type="source"
          position={Position.Left}
          style={{ top: "75%" }}
        />
      )}
    </div>
  );
});
const nodeTypes = { pipeline: PipelineNode };
const edgeTypes = { pipeline: PipelineEdge };

export function PipelineGraph({
  detail,
  expanded,
  inspect,
  expand,
  previewGroup,
}: {
  detail: QueueRunDetail;
  expanded: ReadonlySet<string>;
  inspect: (stage: MonitorStage, pin: boolean) => void;
  expand: (group: string) => void;
  previewGroup: (group: string) => void;
}) {
  const graph = useMemo(
    () => buildPipelineGraph(detail, expanded),
    [detail, expanded],
  );
  const interactions = useMemo(
    () => ({ inspect, expand, previewGroup }),
    [inspect, expand, previewGroup],
  );
  return (
    <Interactions.Provider value={interactions}>
      <div
        className="h-[380px] rounded-lg border bg-muted/10"
        aria-label="Echo pipeline diagram"
      >
        <ReadOnlyFlow
          nodes={graph.nodes}
          edges={graph.edges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          fitView
          fitViewOptions={{
            padding: 0.2,
            minZoom: 0.65,
            maxZoom: 1,
            nodes: graph.nodes.filter((node) =>
              ["running", "retrying", "waiting", "queued"].includes(
                node.data.state,
              ),
            ).length
              ? graph.nodes.filter((node) =>
                  ["running", "retrying", "waiting", "queued"].includes(
                    node.data.state,
                  ),
                )
              : graph.nodes
                  .filter((node) => node.data.stage || node.data.count)
                  .slice(0, 3),
          }}
        />
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        Solid lines show dependencies. Dashed lines show contained work.
      </p>
    </Interactions.Provider>
  );
}
